import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faUsers, faDollarSign, faGamepad, faVideo, faComments, faArrowRight } from '@fortawesome/free-solid-svg-icons';
import Header from '../../component/layout/header';
import Footer from '../../component/layout/footer';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Navigate from '../../../../util/Navigate';
import { faXTwitter } from '@fortawesome/free-brands-svg-icons';

function PublishersCommunityPage() {
  return (
    <>
      <Helmet prioritizeSeoTags>
        <meta charSet="utf-8" />
        <title>Build and Monetize Your Gaming Community | Glitch Gaming</title>
        <meta
          name="description"
          content="Learn how to create a vibrant gaming community with tools for live streaming, revenue generation, user engagement, and more."
        />
        <meta property="og:title" content="Build and Monetize Your Gaming Community | Glitch" />
        <meta property="og:description" content="Learn how to create a vibrant gaming community with tools for live streaming, revenue generation, user engagement, and more." />
        <meta property="og:image" content="https://www.glitch.fun/assets/images/publishers/publishers/community_banner.webp" />
      </Helmet>
      <Header />

      {/* Hero Section */}
      <section className="community-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6 text-center text-lg-start">
              <h1 className="display-4 fw-bold mb-4 pt-5">
                Build & Monetize Your <span className="text-gradient">Gaming Community</span>
              </h1>
              <p className="lead mb-5">Combine tools for streaming, user engagement, and revenue generation into one powerful platform.</p>
              <div className="cta-group">
                <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-primary btn-lg me-3 mb-3">
                  Get Started Now <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
                </Link>
                <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg mb-3">
                  Book Demo
                </Link>
              </div>
            </div>
            <div className="col-lg-6 pt-5">
              <div className="hero-visual mt-5 mt-lg-0">
                <img
                  src="/assets/images/publishers/community_banner.webp"
                  alt="Gaming Community"
                  className="img-fluid rounded-3 shadow-lg border border-3 border-white"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="community-features py-5 text-black">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3 text-black">Optimized for Gaming Communities</h2>
            <p className="lead">Everything you need to grow and engage your audience</p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-4">
              <div className="feature-card h-100">
                <div className="icon-wrapper bg-primary">
                  <FontAwesomeIcon icon={faGamepad} className="fa-2x" />
                </div>
                <h3 className='text-black'>Live Streaming</h3>
                <p>Enable users to live stream their gameplay, connecting with fans and building their audience.</p>
                <ul className="feature-list">
                  <li>Low latency streaming</li>
                  <li>Multi-platform support</li>
                  <li>Built-in chat system</li>
                </ul>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-card h-100">
                <div className="icon-wrapper bg-success">
                  <FontAwesomeIcon icon={faXTwitter} className="fa-2x" />
                </div>
                <h3 className='text-black'>Short Form Content</h3>
                <p>Have your fans create engaging short-form content like memes, clips, and more.</p>
                <ul className="feature-list">
                  <li>Easy content creation tools</li>
                  <li>Built-in sharing</li>
                  <li>Viral potential</li>
                </ul>
              </div>
            </div>
            <div className="col-md-4">
              <div className="feature-card h-100">
                <div className="icon-wrapper bg-warning">
                  <FontAwesomeIcon icon={faUsers} className="fa-2x" />
                </div>
                <h3 className='text-black'>User Engagement</h3>
                <p>Boost interaction with features like live chat, private messaging, and user profiles.</p>
                <ul className="feature-list">
                  <li>Community forums</li>
                  <li>Gamification elements</li>
                  <li>Notification system</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="content-section py-5 bg-dark text-white">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-6">
              <h2 className="display-5 fw-bold mb-4">Leverage User-Generated Content</h2>
              <p className="lead mb-4">Turn your biggest fans into content creators and brand ambassadors.</p>
              
              <div className="content-point mb-4">
                <div className="point-icon bg-primary rounded-circle d-flex align-items-center justify-content-center">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div className="point-content">
                  <h4 className='text-white'>Increase Retention</h4>
                  <p>Keep users engaged with content outside the game.</p>
                </div>
              </div>
              
              <div className="content-point mb-4">
                <div className="point-icon bg-success rounded-circle d-flex align-items-center justify-content-center">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="point-content">
                  <h4 className='text-white'>Highlight Members</h4>
                  <p>Showcase your super fans to make them feel special.</p>
                </div>
              </div>
              
              <div className="content-point">
                <div className="point-icon bg-warning rounded-circle d-flex align-items-center justify-content-center">
                  <FontAwesomeIcon icon={faVideo} />
                </div>
                <div className="point-content">
                  <h4 className='text-white'>Re-Share Content</h4>
                  <p>Amplify user content to drive growth and awareness.</p>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="content-visual mt-5 mt-lg-0">
                <img
                  src="/assets/images/features/social-media.png"
                  alt="User Generated Content"
                  className="img-fluid rounded-3 shadow-lg"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Monetization Section */}
      <section className="monetization-section py-5 text-black">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-5 fw-bold mb-3 text-black">Monetization Options</h2>
            <p className="lead">Multiple ways to generate revenue from your community</p>
          </div>
          
          <div className="row g-4">
            <div className="col-md-4">
              <div className="monetization-card h-100">
                <div className="card-header bg-primary text-white p-4 rounded-top">
                  <h3 className="mb-0">Ads</h3>
                </div>
                <div className="card-body p-4">
                  <ul className="monetization-list">
                    <li>Banner Ads</li>
                    <li>Video Pre/Mid/Post-Roll</li>
                    <li>Interactive Ads</li>
                    <li>Sponsored Content</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="monetization-card h-100">
                <div className="card-header bg-success text-white p-4 rounded-top">
                  <h3 className="mb-0">Donations</h3>
                </div>
                <div className="card-body p-4">
                  <ul className="monetization-list">
                    <li>On-Screen Donations</li>
                    <li>Monthly Subscriptions</li>
                    <li>Tip Jars</li>
                    <li>Pay-Per-View</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="monetization-card h-100">
                <div className="card-header bg-warning text-white p-4 rounded-top">
                  <h3 className="mb-0">Commerce</h3>
                </div>
                <div className="card-body p-4">
                  <ul className="monetization-list">
                    <li>Merchandise Sales</li>
                    <li>In-App Purchases</li>
                    <li>Affiliate Marketing</li>
                    <li>Live Shopping</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
          
          <div className="text-center mt-5">
            <Link to="/revenue" className="btn btn-primary btn-lg px-5">
              Explore Monetization <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="community-cta py-5">
        <div className="container text-center">
          <h2 className="mb-4 display-5 fw-bold">Ready to Build Your Community?</h2>
          <p className="cta-subtitle mb-5">Join our platform to create, engage, and monetize your gaming community.</p>
          <div className="cta-wrapper">
            <Link to={Navigate.publishersOnboardingMultistepPage()} className="btn btn-primary btn-lg px-5 py-3 me-3">
              Get Started Now
            </Link>
            <Link to={Navigate.demoPage()} className="btn btn-outline-light btn-lg px-5 py-3">
              Request Demo
            </Link>
          </div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .community-hero {
          padding: 100px 0;
          background: linear-gradient(135deg, #1a1b2f 0%, #080e37 100%);
          color: white;
          position: relative;
          overflow: hidden;
        }

        .text-gradient {
          background: linear-gradient(90deg, #ff0052 0%, #9400ff 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
        }

        .hero-visual {
          position: relative;
          border-radius: 20px;
          overflow: hidden;
          transform: perspective(1000px) rotateY(-5deg);
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
        }

        .community-features {
          padding: 80px 0;
          background: #f8f9ff;
        }

        .feature-card {
          background: white;
          padding: 2rem;
          border-radius: 16px;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }

        .feature-card:hover {
          transform: translateY(-10px);
        }

        .icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 1.5rem;
        }

        .feature-list {
          list-style: none;
          padding-left: 0;
        }

        .feature-list li {
          padding: 0.5rem 0;
          position: relative;
          padding-left: 1.5rem;
        }

        .feature-list li:before {
          content: "✓";
          position: absolute;
          left: 0;
          color: var(--bs-primary);
          font-weight: bold;
        }

        .content-section {
          padding: 100px 0;
          background: linear-gradient(135deg, #080e37 0%, #1a1b2f 100%);
        }

        .content-point {
          display: flex;
          align-items: flex-start;
          margin-bottom: 1.5rem;
        }

        .point-icon {
          width: 50px;
          height: 50px;
          flex-shrink: 0;
          margin-right: 1.5rem;
        }

        .content-visual {
          border-radius: 20px;
          overflow: hidden;
          box-shadow: 0 25px 50px -12px rgba(0,0,0,0.25);
        }

        .monetization-section {
          padding: 100px 0;
          background: white;
        }

        .monetization-card {
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1);
          transition: transform 0.3s ease;
        }

        .monetization-card:hover {
          transform: translateY(-10px);
        }

        .monetization-list {
          list-style: none;
          padding-left: 0;
        }

        .monetization-list li {
          padding: 0.5rem 0;
          position: relative;
          padding-left: 1.5rem;
        }

        .monetization-list li:before {
          content: "•";
          position: absolute;
          left: 0;
          color: var(--bs-primary);
          font-weight: bold;
          font-size: 1.5rem;
          line-height: 1;
        }

        .community-cta {
          padding: 100px 0;
          background: linear-gradient(135deg, #ff0052 0%, #9400ff 100%);
          color: white;
        }

        @media (max-width: 768px) {
          .community-hero, .content-section, .monetization-section, .community-cta {
            padding: 60px 0;
          }
          
          .hero-visual {
            transform: none;
          }
        }
      `}</style>
    </>
  );
}

export default PublishersCommunityPage;